import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AccountComplianceDto, ComplianceControllerService } from '@api/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { DateTimeService } from '@shared/datetime/datetime.service';
import { createCsvFile } from '@shared/export/export-csv';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { parseDateTimeString } from '@shared/utils/datetime';
import { saveAs } from 'file-saver';
import { floor, sum, take } from 'lodash';
import { filter, mergeMap, pipe } from 'rxjs';
import { AddPeriodDialogComponent } from '../../leaderboard/components/add-period-dialog/add-period-dialog.component';
import { PeriodModel } from '../../leaderboard/store/leaderboard.interfaces';
import { getInitialPeriods, toPeriodModel } from '../../leaderboard/store/leaderboard.utils';

export const COMPLIANCE_LEADERBOARD_ROUTE_ID = 'complianceLeaderboardRouteId';

interface ComplianceAccountEntry {
  volume: number;
  accountName: string;
  accountType: AccountComplianceDto.AccountTypeEnum;
}

interface CompliancePeriodDataModel {
  [periodId: string]: {
    [accountId: number]: ComplianceAccountEntry;
  };
}

export type ComplianceLeaderboardRowModel = {
  accountId: number;
  accountName: string;
  accountType: AccountComplianceDto.AccountTypeEnum;
} & {
  [dataFieldKey: string]: number | string;
};

const initialPeriods = getInitialPeriods();

export const ComplianceLeaderboardStore = signalStore(
  { providedIn: 'root' },
  withState({
    periods: initialPeriods,
    selectedPeriodsIds: take(
      initialPeriods.map(p => p.id),
      3,
    ),
    periodsData: {} as CompliancePeriodDataModel,
  }),
  withProps(() => ({
    _complianceService: inject(ComplianceControllerService),
    _dateTimeService: inject(DateTimeService),
    _notificationService: inject(NotificationService),
    _dialogService: inject(MatDialog),
    _injector: inject(Injector),
  })),
  withComputed(store => {
    const periodsToDisplay = computed(() => store.periods().filter(p => store.selectedPeriodsIds().includes(p.id)));

    const leaderboard = computed((): ComplianceLeaderboardRowModel[] => {
      const periodsIds = store.selectedPeriodsIds();
      const periodsData = store.periodsData();

      const accountsMap = new Map<number, { accountName: string; accountType: AccountComplianceDto.AccountTypeEnum }>();
      for (const periodId of periodsIds) {
        const periodData = periodsData[periodId];
        if (periodData) {
          for (const [id, data] of Object.entries(periodData)) {
            if (!accountsMap.has(Number(id))) {
              accountsMap.set(Number(id), {
                accountName: data.accountName,
                accountType: data.accountType,
              });
            }
          }
        }
      }

      return Array.from(accountsMap.entries()).map(([accountId, { accountName, accountType }]) => {
        const entry: ComplianceLeaderboardRowModel = {
          accountId,
          accountName,
          accountType,
        };

        return periodsIds.reduce((result, periodId) => {
          result[`${periodId}-volume`] = periodsData[periodId]?.[accountId]?.volume ?? 0;
          return result;
        }, entry);
      });
    });

    const totals = computed(() => {
      const data = leaderboard();
      const periodsIds = store.selectedPeriodsIds();
      const dataWithoutHouseLP = data.filter(row => row.accountType !== 'HOUSE_LP');

      return periodsIds.reduce(
        (result, periodId) => {
          const volumeColumn = `${periodId}-volume`;
          result[`${volumeColumn}-total`] = floor(sum(dataWithoutHouseLP.map(row => row[volumeColumn])), 2);
          return result;
        },
        {} as { [key: string]: number },
      );
    });

    const columns = computed(() => ['accountName', ...periodsToDisplay().map(p => `${p.id}-volume`)]);

    return { periodsToDisplay, leaderboard, totals, columns };
  }),
  withMethods(store => {
    const updateSelectedPeriodsIds = (periodsIds: string[]) => {
      patchState(store, { selectedPeriodsIds: periodsIds });
    };

    const loadLeaderboard = rxMethod<PeriodModel[]>(
      pipe(
        filter(periods => periods.length > 0),
        mergeMap(periods =>
          store._complianceService
            .getVolumeStatisticForPeriods1({
              fromTime: periods.map(period => store._dateTimeService.toAPIDate(period.fromTime)),
              toTime: periods.map(period => store._dateTimeService.toAPIDate(period.toTime)),
            })
            .pipe(
              tapResponse({
                next: periodsStatistics => {
                  const loadedPeriodsData: CompliancePeriodDataModel = {};
                  periods.forEach((period, index) => {
                    const periodData: { [accountId: number]: ComplianceAccountEntry } = {};
                    for (const stat of periodsStatistics[index].statistics) {
                      periodData[stat.account.id] = {
                        volume: stat.volumeInUsd,
                        accountName: stat.account.name,
                        accountType: stat.account.accountType,
                      };
                    }
                    loadedPeriodsData[period.id] = periodData;
                  });
                  patchState(store, state => ({
                    periodsData: { ...state.periodsData, ...loadedPeriodsData },
                  }));
                },
                error: (error: HttpErrorResponse) => {
                  store._notificationService.showErrorNotification(`The leaderboard couldn't be loaded`, error);
                },
              }),
            ),
        ),
      ),
    );

    const addPeriod = (fromTime: string, toTime: string, name: string) => {
      const dateRange = {
        fromTime: parseDateTimeString(fromTime),
        toTime: parseDateTimeString(toTime),
      };
      const newPeriod = toPeriodModel(dateRange, name);

      patchState(store, state => ({
        periods: [...state.periods, newPeriod],
        selectedPeriodsIds: [...state.selectedPeriodsIds, newPeriod.id],
      }));
      loadLeaderboard([newPeriod]);
    };

    const refresh = () => {
      loadLeaderboard(store.periodsToDisplay());
    };

    const exportToCsv = () => {
      const periods = store.periodsToDisplay();
      const rows = store.leaderboard();
      if (!rows.length) {
        return;
      }

      const csv = createCsvFile<ComplianceLeaderboardRowModel>({
        headers: [
          { fieldKey: 'accountName', fieldName: 'Account name' },
          ...periods.map(p => ({
            fieldKey: `${p.id}-volume` as keyof ComplianceLeaderboardRowModel,
            fieldName: `${p.name} (Volume)`,
          })),
        ],
        rows,
      });

      saveAs(csv, 'compliance-leaderboard.csv');
    };

    const openAddPeriodDialog = () => {
      store._dialogService
        .open(AddPeriodDialogComponent, { injector: store._injector })
        .afterClosed()
        .subscribe((result?: { fromTime: string; toTime: string; name: string }) => {
          if (result) {
            addPeriod(result.fromTime, result.toTime, result.name);
          }
        });
    };

    return {
      updateSelectedPeriodsIds,
      addPeriod,
      loadLeaderboard,
      refresh,
      openAddPeriodDialog,
      exportToCsv,
    };
  }),
  withHooks({
    onInit(store) {
      store.loadLeaderboard(store.periods());
    },
  }),
);
